import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Plus, Save, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, fmtMoney, useApi } from "@/lib/api";
import { TemplateManager } from "@/components/TemplateManager";
import { CategoryManager } from "@/components/CategoryManager";

export const IGNORE = "__ignore__";
const NEW = "__new__";

function NewCategory({ open, onClose, onCreated }) {
  const api = useApi();
  const [name, setName] = useState("");
  const [side, setSide] = useState("asset");
  useEffect(() => { if (open) { setName(""); setSide("asset"); } }, [open]);
  const save = async () => {
    try {
      const r = await api.post("/categories", { name, side });
      toast.success(`Category "${r.data.name}" added`);
      onCreated(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tb-new-category-dialog">
        <DialogHeader><DialogTitle>New category</DialogTitle></DialogHeader>
        <label className="small-label" htmlFor="nc-name">Name</label>
        <Input id="nc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Real Estate" className="rounded-sm" data-testid="tb-new-category-name" />
        <label className="small-label mt-3">Type</label>
        <Select value={side} onValueChange={setSide}>
          <SelectTrigger className="rounded-sm" data-testid="tb-new-category-side"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="asset">Asset</SelectItem>
            <SelectItem value="liability">Liability</SelectItem>
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={name.trim().length < 2} data-testid="tb-new-category-save">Add category</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaveTemplate({ open, onClose, onSave }) {
  const [name, setName] = useState("");
  useEffect(() => { if (open) setName(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tb-save-template-dialog">
        <DialogHeader><DialogTitle>Save mapping as a template</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. QuickBooks equipment LLC" className="rounded-sm" data-testid="tb-template-name-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(name)} disabled={name.trim().length < 2} data-testid="tb-template-save-confirm">Save template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const CategorySelect = ({ value, cats, onChange, testid, optional }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger className={`h-8 rounded-sm text-xs min-w-0 [&>span]:truncate ${value || optional ? "bg-white" : "bg-amber-50 border-amber-300"}`} data-testid={testid}>
      <SelectValue placeholder={optional ? "Optional (zero balance)" : "Choose category"} />
    </SelectTrigger>
    <SelectContent className="max-h-80">
      <SelectGroup>
        <SelectLabel>Assets</SelectLabel>
        {cats.assets.map((c) => <SelectItem key={`a-${c.name}`} value={c.name}>{c.name}</SelectItem>)}
      </SelectGroup>
      <SelectSeparator />
      <SelectGroup>
        <SelectLabel>Liabilities</SelectLabel>
        {cats.liabilities.map((c) => <SelectItem key={`l-${c.name}`} value={c.name}>{c.name}</SelectItem>)}
      </SelectGroup>
      <SelectSeparator />
      <SelectItem value={IGNORE}>Not included (equity, income, expense)</SelectItem>
      <SelectItem value={NEW}>+ New category…</SelectItem>
    </SelectContent>
  </Select>
);

function MappingTable({ accounts, mappings, cats, setMap, onNewCat, quicken }) {
  return (
    <div className="panel overflow-x-auto mt-6" data-testid="tb-mapping-table">
      <table className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="text-left small-label border-b border-slate-200">
            {quicken ? (
              <><th className="px-4 py-3">Quicken account</th><th colSpan={2}>Quicken group</th></>
            ) : (
              <><th className="px-4 py-3">Trial balance account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></>
            )}
            <th className="text-right pr-4">{quicken ? "Amount" : "Net"}</th><th className="w-[300px] pr-4">AVORAlogic category</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a, k) => {
            const v = mappings[a.name];
            const zero = Math.abs(a.net) < 0.005;
            return (
              <tr key={a.name} className={`border-b border-slate-100 ${!v && !zero ? "bg-amber-50/50" : ""}`} data-testid={`tb-row-${k}`}>
                <td className="px-4 py-2">{a.name}{!v && a.suggested && <span className="ml-2 text-[10px] font-mono uppercase text-amber-700">suggest: {a.suggested === IGNORE ? "not included" : a.suggested}</span>}</td>
                {quicken ? (
                  <td colSpan={2} className="text-xs text-slate-500">{a.group}</td>
                ) : (
                  <><td className="num text-right text-xs">{a.debit ? fmtMoney(a.debit) : ""}</td>
                  <td className="num text-right text-xs">{a.credit ? fmtMoney(a.credit) : ""}</td></>
                )}
                <td className={`num text-right text-xs pr-4 ${zero ? "text-slate-400" : ""}`}>{fmtMoney(a.net)}</td>
                <td className="py-1.5 pr-4">
                  <CategorySelect value={v} cats={cats} optional={zero} testid={`tb-map-${k}`} onChange={(c) => (c === NEW ? onNewCat(a.name) : setMap(a.name, c))} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Summary({ accounts, mappings, cats }) {
  const side = useMemo(() => Object.fromEntries([...cats.assets.map((c) => [c.name, "asset"]), ...cats.liabilities.map((c) => [c.name, "liability"])]), [cats]);
  let assets = 0, liabs = 0, unmapped = 0;
  accounts.forEach((a) => {
    const c = mappings[a.name];
    if (!c) { if (Math.abs(a.net) > 0.004) unmapped += 1; return; }
    if (side[c] === "asset") assets += a.net;
    if (side[c] === "liability") liabs -= a.net;
  });
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="tb-summary">
      {[["Assets", fmtMoney(assets, true), "tb-sum-assets"], ["Liabilities", fmtMoney(liabs, true), "tb-sum-liabilities"],
        ["Net assets", fmtMoney(assets - liabs, true), "tb-sum-net"], ["Unmapped with balance", String(unmapped), "tb-sum-unmapped"]].map(([l, v, t]) => (
        <div key={l} className="panel p-4"><div className="small-label">{l}</div><div className={`num text-lg mt-1 ${t === "tb-sum-unmapped" && unmapped ? "text-amber-700" : ""}`} data-testid={t}>{v}</div></div>
      ))}
    </div>
  );
}

export function TrialBalanceUpload({ entities, periods = [], onDone, format = "trialbalance" }) {
  const quicken = format === "quicken";
  const label = quicken ? "Quicken export" : "trial balance";
  const api = useApi();
  const [file, setFile] = useState(null);
  const [tb, setTb] = useState(null);
  const [entityId, setEntityId] = useState("");
  const [period, setPeriod] = useState("");
  const [cats, setCats] = useState({ assets: [], liabilities: [] });
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [mappings, setMappings] = useState({});
  const [newCatFor, setNewCatFor] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadMeta = () => Promise.all([api.get("/categories"), api.get("/tb/templates", { params: { format } })]).then(([c, t]) => { setCats(c.data); setTemplates(t.data); return t.data; });
  useEffect(() => { loadMeta(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTemplate = (id, list = templates) => {
    setTplId(id);
    const t = list.find((x) => x.id === id);
    if (t) setMappings(t.mappings || {});
  };
  const pick = async (f) => {
    if (!f) return;
    setFile(f);
    try {
      const r = await api.post("/tb/inspect", (() => { const fd = new FormData(); fd.append("file", f); fd.append("format", format); return fd; })());
      setTb(r.data);
      if (r.data.period) setPeriod(r.data.period);
      toast.message(`${r.data.accounts.length} accounts found`);
    } catch (e) {
      setTb(null);
      toast.error(errMsg(e));
    }
  };
  const chooseEntity = (id) => {
    setEntityId(id);
    if (!tplId && Object.keys(mappings).length === 0) {
      const t = templates.find((x) => (x.entity_ids || []).includes(id));
      if (t) { applyTemplate(t.id); toast.message(`Using template "${t.name}" last used for this entity`); }
    }
  };
  const setMap = (name, cat) => setMappings((m) => ({ ...m, [name]: cat }));
  const applySuggestions = () => setMappings((m) => {
    const n = { ...m };
    tb.accounts.forEach((a) => { if (!n[a.name] && a.suggested) n[a.name] = a.suggested; });
    return n;
  });
  const saveTemplate = async (name) => {
    try {
      const r = tplId && !name
        ? await api.put(`/tb/templates/${tplId}`, { name: templates.find((t) => t.id === tplId).name, mappings })
        : await api.post("/tb/templates", { name, mappings, format });
      const list = await loadMeta();
      setTplId(r.data.id);
      setSaveOpen(false);
      toast.success(name ? "Template saved" : "Template updated");
      return list;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const removeTemplate = async () => {
    const t = templates.find((x) => x.id === tplId);
    if (!t || !window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/tb/templates/${tplId}`);
      setTplId("");
      await loadMeta();
      toast.success("Template deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const submit = async () => {
    if (tb.period && tb.period !== period && !window.confirm(`The file says ${tb.period}, but you chose ${period}. Import for ${period}?`)) return;
    if (!periods.includes(period) && !window.confirm(`No other uploads exist for ${period}. This becomes a new month end, and other entities will show no balances for it. Continue?`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_id", entityId);
      fd.append("period", period);
      fd.append("mappings", JSON.stringify(mappings));
      if (tplId) fd.append("template_id", tplId);
      fd.append("format", format);
      const r = await api.post("/tb/import", fd);
      toast.success(`Imported ${r.data.row_count} balances for ${r.data.entity_name}${r.data.replaced ? " (replaced the earlier ${label})" : ""}`);
      setFile(null); setTb(null);
      onDone(period);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const unmapped = tb ? tb.accounts.filter((a) => !mappings[a.name] && Math.abs(a.net) > 0.004).length : 0;
  return (
    <div data-testid="tb-upload">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-end">
        <label className="panel p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors duration-200 lg:col-span-1" data-testid="tb-dropzone">
          <FileSpreadsheet className="h-5 w-5 text-slate-500 shrink-0" />
          <span className="text-sm truncate">{file ? file.name : quicken ? "Choose Quicken Net Worth export (.csv or .xlsx)" : "Choose trial balance (.xlsx)"}</span>
          <input type="file" accept={quicken ? ".csv,.xlsx" : ".xlsx,.xlsm"} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="tb-file-input" />
        </label>
        <div>
          <div className="small-label mb-2">Entity</div>
          <Select value={entityId} onValueChange={chooseEntity}>
            <SelectTrigger className="rounded-sm bg-white [&>span]:truncate" data-testid="tb-entity-select"><SelectValue placeholder="Which entity is this?" /></SelectTrigger>
            <SelectContent className="max-h-80">{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="small-label mb-2">Month end</div>
          <Input type="date" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-sm bg-white" data-testid="tb-month-end-input" />
        </div>
        <div>
          <div className="small-label mb-2">Mapping template</div>
          <div className="flex gap-2">
            <Select value={tplId} onValueChange={(v) => applyTemplate(v)}>
              <SelectTrigger className="rounded-sm bg-white min-w-0 [&>span]:truncate" data-testid="tb-template-select"><SelectValue placeholder={templates.length ? "Choose a template" : "No templates yet"} /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            {tplId && <Button variant="ghost" size="icon" onClick={removeTemplate} data-testid="tb-template-delete"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
      </div>

      {tb && (
        <>
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <Button variant="outline" className="rounded-sm" onClick={applySuggestions} data-testid="tb-apply-suggestions"><Sparkles className="h-4 w-4 mr-2" />Fill blanks with suggestions</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setNewCatFor("")} data-testid="tb-new-category-button"><Plus className="h-4 w-4 mr-2" />New category</Button>
            {tplId && <Button variant="outline" className="rounded-sm" onClick={() => saveTemplate()} data-testid="tb-template-update"><Save className="h-4 w-4 mr-2" />Update template</Button>}
            <Button variant="outline" className="rounded-sm" onClick={() => setSaveOpen(true)} data-testid="tb-template-save-new"><Save className="h-4 w-4 mr-2" />Save as new template</Button>
            {quicken ? (
              <span className={`text-xs ml-auto ${tb.reported_net_worth == null || Math.abs(tb.total_debit - tb.total_credit - tb.reported_net_worth) < 0.05 ? "text-emerald-700" : "text-red-700"}`} data-testid="tb-balance-check">
                Assets {fmtMoney(tb.total_debit)} · Liabilities {fmtMoney(tb.total_credit)}{tb.reported_net_worth != null && ` · Quicken net worth ${fmtMoney(tb.reported_net_worth)}`}
              </span>
            ) : (
            <span className={`text-xs ml-auto ${Math.abs(tb.total_debit - tb.total_credit) < 0.05 ? "text-emerald-700" : "text-red-700"}`} data-testid="tb-balance-check">
              Debits {fmtMoney(tb.total_debit)} · Credits {fmtMoney(tb.total_credit)}
            </span>
            )}
          </div>
          <Summary accounts={tb.accounts} mappings={mappings} cats={cats} />
          <MappingTable quicken={quicken} accounts={tb.accounts} mappings={mappings} cats={cats} setMap={setMap} onNewCat={(n) => setNewCatFor(n)} />
          <div className="flex flex-wrap items-center gap-4 mt-6">
            <Button onClick={submit} disabled={busy || !entityId || !period || unmapped > 0} className="rounded-sm h-11 px-8" data-testid="tb-import-button">{busy ? "Importing…" : quicken ? "Import Quicken export" : "Import trial balance"}</Button>
            <span className="text-xs text-slate-500">{unmapped ? `Map the ${unmapped} highlighted account(s) with balances first.` : "Several accounts can map to one category; they're added together. Accounts marked Not included are left out."}</span>
          </div>
        </>
      )}
      <NewCategory open={newCatFor !== null} onClose={() => setNewCatFor(null)}
        onCreated={async (c) => { await loadMeta(); if (typeof newCatFor === "function") newCatFor(c.name); else if (newCatFor) setMap(newCatFor, c.name); setNewCatFor(null); }} />
      <SaveTemplate open={saveOpen} onClose={() => setSaveOpen(false)} onSave={(n) => saveTemplate(n)} />
      <TemplateManager format={format} templates={templates} cats={cats} reload={async () => { const l = await loadMeta(); const t = l.find((x) => x.id === tplId); if (t && tb) setMappings(t.mappings || {}); }}
        onNewCat={(cb) => setNewCatFor(() => cb)} onDeleted={(id) => { if (id === tplId) setTplId(""); }} />
      <CategoryManager cats={cats} onAdd={() => setNewCatFor("")} reload={loadMeta} />
    </div>
  );
}
